/**
 * Sistema de diagnóstico avanzado para líneas de referencia
 * Permite verificar qué método se está usando realmente y detectar problemas
 */

class ReferenceLinesDiagnostic {
  constructor() {
    this.observers = []
    this.methodHistory = []
    this.performanceMetrics = {
      customMethodCalls: 0,
      cornerstoneMethodCalls: 0,
      lastMethodChange: null
    }
  }

  // Verificar si CornerstoneTools está realmente disponible y funcionando
  checkCornerstoneToolsAvailability() {
    const checks = {
      globalAvailable: !!window.cornerstoneTools,
      referenceLinesToolExists: false,
      referenceLinesToolEnabled: false,
      synchronizerExists: false,
      overlayExists: false
    }

    if (window.cornerstoneTools) {
      try {
        // Verificar si la herramienta ReferenceLines existe
        const referenceLinesTool = window.cornerstoneTools.getTool('ReferenceLines')
        checks.referenceLinesToolExists = !!referenceLinesTool

        // Verificar si está habilitada
        checks.referenceLinesToolEnabled = referenceLinesTool ? 
          window.cornerstoneTools.getToolEnabled('ReferenceLines') : false

        // Verificar si hay un sincronizador activo
        checks.synchronizerExists = !!window.cornerstoneTools.synchronizers

        // Verificar si hay overlays (esto es clave para el comportamiento suave)
        const elements = document.querySelectorAll('.cornerstone-enabled-image')
        checks.overlayExists = elements.length > 0 && 
          elements[0].querySelector('.cornerstone-overlay') !== null

      } catch (error) {
        checks.error = error.message
      }
    }

    return checks
  }

  // Verificar el método personalizado
  checkCustomMethod() {
    const checks = {
      customMethodActive: false,
      customLinesDrawn: false,
      canvasElements: 0,
      svgElements: 0
    }

    // Buscar elementos canvas que podrían ser del método personalizado
    const canvases = document.querySelectorAll('canvas')
    checks.canvasElements = canvases.length

    // Buscar elementos SVG que podrían ser líneas de referencia
    const svgs = document.querySelectorAll('svg')
    checks.svgElements = svgs.length

    // Verificar si hay líneas dibujadas manualmente
    const lines = document.querySelectorAll('line, path')
    checks.customLinesDrawn = lines.length > 0

    return checks
  }

  // Monitorear cambios en el DOM para detectar qué método está dibujando
  startDOMMonitoring() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Verificar si se agregaron elementos de CornerstoneTools
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && node.classList.contains('cornerstone-overlay')) {
                this.performanceMetrics.cornerstoneMethodCalls++
                this.methodHistory.push({
                  timestamp: Date.now(),
                  method: 'CORNERSTONE_TOOLS',
                  action: 'overlay_added',
                  element: node
                })
              }
            }
          })

          // Verificar si se agregaron elementos del método personalizado
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName === 'LINE' || node.tagName === 'PATH') {
                this.performanceMetrics.customMethodCalls++
                this.methodHistory.push({
                  timestamp: Date.now(),
                  method: 'CUSTOM',
                  action: 'line_added',
                  element: node
                })
              }
            }
          })
        }
      })
    })

    // Observar cambios en el body
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    this.observers.push(observer)
  }

  // Detectar parpadeos en el cambio de imágenes
  detectImageFlickering() {
    const viewports = document.querySelectorAll('.cornerstone-enabled-image')
    const flickeringData = []

    viewports.forEach((viewport, index) => {
      const canvas = viewport.querySelector('canvas')
      if (canvas) {
        // Monitorear cambios en el canvas
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
              flickeringData.push({
                viewport: index,
                timestamp: Date.now(),
                change: 'style_change',
                canvas: canvas
              })
            }
          })
        })

        observer.observe(canvas, {
          attributes: true,
          attributeFilter: ['style', 'width', 'height']
        })

        this.observers.push(observer)
      }
    })

    return flickeringData
  }

  // Análisis completo del estado actual
  getCompleteAnalysis() {
    const cornerstoneChecks = this.checkCornerstoneToolsAvailability()
    const customChecks = this.checkCustomMethod()
    
    return {
      timestamp: Date.now(),
      cornerstoneTools: cornerstoneChecks,
      customMethod: customChecks,
      performance: this.performanceMetrics,
      methodHistory: this.methodHistory.slice(-10), // Últimos 10 cambios
      recommendations: this.generateRecommendations(cornerstoneChecks, customChecks)
    }
  }

  // Generar recomendaciones basadas en el análisis
  generateRecommendations(cornerstoneChecks, customChecks) {
    const recommendations = []

    if (!cornerstoneChecks.globalAvailable) {
      recommendations.push({
        type: 'error',
        message: 'CornerstoneTools no está disponible globalmente. Solo se puede usar el método personalizado.'
      })
    }

    if (cornerstoneChecks.globalAvailable && !cornerstoneChecks.referenceLinesToolExists) {
      recommendations.push({
        type: 'warning',
        message: 'CornerstoneTools está disponible pero la herramienta ReferenceLines no existe.'
      })
    }

    if (cornerstoneChecks.globalAvailable && cornerstoneChecks.referenceLinesToolExists && !cornerstoneChecks.referenceLinesToolEnabled) {
      recommendations.push({
        type: 'warning',
        message: 'La herramienta ReferenceLines existe pero no está habilitada.'
      })
    }

    if (!cornerstoneChecks.overlayExists && cornerstoneChecks.globalAvailable) {
      recommendations.push({
        type: 'info',
        message: 'No se detectaron overlays de CornerstoneTools. Esto podría explicar por qué las líneas no son suaves.'
      })
    }

    if (customChecks.customLinesDrawn) {
      recommendations.push({
        type: 'info',
        message: 'Se detectaron líneas dibujadas manualmente. Probablemente se está usando el método personalizado.'
      })
    }

    return recommendations
  }

  // Imprimir análisis completo en consola
  printCompleteAnalysis() {
    const analysis = this.getCompleteAnalysis()
    
    console.group('🔍 ANÁLISIS COMPLETO DE LÍNEAS DE REFERENCIA')
    console.log('📅 Timestamp:', new Date(analysis.timestamp).toLocaleString())
    
    console.group('📦 CornerstoneTools')
    console.log('✅ Disponible globalmente:', analysis.cornerstoneTools.globalAvailable)
    console.log('🔧 Herramienta ReferenceLines existe:', analysis.cornerstoneTools.referenceLinesToolExists)
    console.log('🎯 Herramienta ReferenceLines habilitada:', analysis.cornerstoneTools.referenceLinesToolEnabled)
    console.log('🔄 Sincronizador existe:', analysis.cornerstoneTools.synchronizerExists)
    console.log('🎨 Overlay existe:', analysis.cornerstoneTools.overlayExists)
    if (analysis.cornerstoneTools.error) {
      console.error('❌ Error:', analysis.cornerstoneTools.error)
    }
    console.groupEnd()

    console.group('🛠️ Método Personalizado')
    console.log('📊 Líneas dibujadas manualmente:', analysis.customMethod.customLinesDrawn)
    console.log('🖼️ Elementos canvas:', analysis.customMethod.canvasElements)
    console.log('📐 Elementos SVG:', analysis.customMethod.svgElements)
    console.groupEnd()

    console.group('📈 Métricas de Rendimiento')
    console.log('🔧 Llamadas a CornerstoneTools:', analysis.performance.cornerstoneMethodCalls)
    console.log('🛠️ Llamadas al método personalizado:', analysis.performance.customMethodCalls)
    console.log('📅 Último cambio de método:', analysis.performance.lastMethodChange)
    console.groupEnd()

    console.group('📋 Recomendaciones')
    analysis.recommendations.forEach((rec, index) => {
      const icon = rec.type === 'error' ? '❌' : rec.type === 'warning' ? '⚠️' : 'ℹ️'
      console.log(`${icon} ${index + 1}. ${rec.message}`)
    })
    console.groupEnd()

    console.groupEnd()
    return analysis
  }

  // Limpiar observadores
  cleanup() {
    this.observers.forEach(observer => observer.disconnect())
    this.observers = []
  }
}

// Crear instancia global
const referenceLinesDiagnostic = new ReferenceLinesDiagnostic()

// Exponer globalmente
if (typeof window !== 'undefined') {
  window.ReferenceLinesDiagnostic = {
    // Análisis completo
    getCompleteAnalysis: () => referenceLinesDiagnostic.getCompleteAnalysis(),
    printCompleteAnalysis: () => referenceLinesDiagnostic.printCompleteAnalysis(),
    
    // Verificaciones específicas
    checkCornerstoneTools: () => referenceLinesDiagnostic.checkCornerstoneToolsAvailability(),
    checkCustomMethod: () => referenceLinesDiagnostic.checkCustomMethod(),
    
    // Monitoreo
    startMonitoring: () => {
      referenceLinesDiagnostic.startDOMMonitoring()
      referenceLinesDiagnostic.detectImageFlickering()
      console.log('🔍 Monitoreo de líneas de referencia iniciado')
    },
    
    // Limpieza
    cleanup: () => referenceLinesDiagnostic.cleanup(),
    
    // Métricas
    getMetrics: () => referenceLinesDiagnostic.performanceMetrics,
    getHistory: () => referenceLinesDiagnostic.methodHistory
  }
  
  console.log('🔧 ReferenceLinesDiagnostic avanzado disponible. Usa ReferenceLinesDiagnostic.printCompleteAnalysis() para análisis completo.')
}

export default referenceLinesDiagnostic
